import {useTranslation} from "react-i18next";
import PageHead from "../../components/PageHead";
import {useEffect, useState} from "react";
import ModalProduct from "./ModalProduct";
import {SortParams, TableColumn} from "../../components/Table";
import axios from "axios";
import {toast} from "react-toastify";
import {getAxiosInstance} from "../../utils/axiosInstance";
import {TFunction} from "i18next";
import SearchTable from "../../components/SearchTable";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";
import {Product} from "./product.type";
import CategorySelect from "../../components/CategorySelect";
import {Icon, Input, InputGroup, InputLeftElement} from "@chakra-ui/react";
import {SearchIcon} from "@chakra-ui/icons";

type ProductRendered = {
    id: string,
    name: string,
    description: string,
    sku: string,
    minQuantity: number,
    createdAt: string,
    updatedAt: string,
    unitMeasurement: string,
};

const getColumns = (t: TFunction<any>): TableColumn[] => [
    {
        Header: t("No"),
        accessor: "no",
        isSortable: true,
    },
    {
        Header: t("NAME"),
        accessor: "name",
        isSortable: true,
    },
    {
        Header: t("DESCRIPTION"),
        accessor: "description",
    },
    {
        Header: t("SKU"),
        accessor: "sku",
    },
    {
        Header: t("PRODUCT_WORK_PRICE"),
        accessor: "productWorkPrice"
    },
    {
        Header: t("PRODUCT_SELLING_PRICE"),
        accessor: "productSellingPrices"
    },
    {
        Header: t("MIN_QUANTITY"),
        accessor: "minQuantity",
    },
    {
        Header: t("Unit of Measurement"),
        accessor: "unitMeasurement",
    },
    {
        Header: t("CREATED_AT"),
        accessor: "createdAt",
        isSortable: true,
    },
    {
        Header: t("UPDATED_AT"),
        accessor: "updatedAt",
        isSortable: true,
    },
];


const Products = () => {
    const {t} = useTranslation("translations");
    const [newModalProduct, setNewModalProduct] = useState<boolean>(false);
    const [length, setLength] = useState<number>(0);
    const [fData, setFData] = useState<ProductRendered[]>([]);

    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [search, setSearch] = useState<string>("");
    const [sortParmas, setSortParams] = useState<SortParams | null>(null);
    const [sku, setSku] = useState<string>("");

    const [mainCategoryId, setMainCategoryId] = useState<string | undefined>();
    const [subCategoriesIds, setSubCategoriesIds] = useState<string[]>([]);

    const navigate = useNavigate();

    const fetchData = async (
        currentPage: number = 1,
        perPage: number = 10,
        search: string | undefined,
        sortParams: SortParams | null,
        searchField?: string,
    ) => {
        try {
            const res = await getAxiosInstance().get('/Products', {
                params: {
                    PageNumber: currentPage,
                    PageSize: perPage,
                    SearchString: search?.toLocaleLowerCase(),
                    SearchField: search ? (searchField ?? "NormalizeName") : undefined,
                    SortBy: sortParams ? sortParams.sortBy : undefined,
                    SortDesc: sortParams ? sortParams.isSortedDesc : undefined,
                    categoryId: mainCategoryId,
                    categoriesIds: subCategoriesIds
                },
                paramsSerializer: {
                    indexes: true,
                }
            });
            const paginationData = JSON.parse(res?.headers.pagination);
            setLength(paginationData.totalItems);
            setFData(formData(res?.data, (paginationData.currentPage - 1) * paginationData.itemsPerPage));

        } catch (error) {
            if (axios.isAxiosError(error)) {
                toast.error(`${error.response?.data}`, {position: toast.POSITION.BOTTOM_RIGHT})
            } else {
                toast.error(`Could not retrieve the products from the database ${error}`, {position: toast.POSITION.BOTTOM_RIGHT});
            }
        }
    }

    const formData = (data: Product[], length: number): ProductRendered[] => {
        const lProduct: ProductRendered[] = [];
        let i = 0;
        for (const a of data) {
            i++;

            let tmp = {
                id: a.id,
                no: i,
                name: a.name,
                description: a.description != null ? a.description : "-",
                minQuantity: a.minQuantity,
                productWorkPrice: a.productWorkPrice ? a.productWorkPrice : "-",
                productProgramingWorkPrice: a.productProgramingWorkPrice ? a.productProgramingWorkPrice : "-",
                productSellingPrices: a.sellingHistoryPrices?.length > 0 ?
                    a.sellingHistoryPrices.sort((a, b) => {
                        // @HACK : new Date() bitches at me when I call it with a string
                        // @ts-ignore
                        return Number(new Date(a.price.createdAt) - new Date(b.price.createdAt)) > 0 ? -1 : 1;
                    })[0].price.value + " (RON)" :
                    "-",
                sku: a.sku,
                createdAt: dayjs(a.createdAt).format("DD-MM-YYYY hh:mm"),
                updatedAt: dayjs(a.updatedAt).format("DD-MM-YYYY hh:mm"),
                unitMeasurement: a.unitMeasurement.name,
            }
            lProduct.push(tmp);
        }

        return lProduct;
    }

    useEffect(() => {
        fetchData(1, 10, search, sortParmas).then();
    }, [mainCategoryId, subCategoriesIds]);

    useEffect(() => {
        if (sku.length >= 1) {
            fetchData(1, 10, sku, sortParmas, "SKU").then();
        } else {
            fetchData(1, 10, "", null);
        }
    }, [sku]);

    return (
        <>
            <PageHead
                addTooltip={t("Add Product")}
                name={t("Product")}
                onAddModal={() => {
                    setNewModalProduct(true)
                }}
            />

            <ModalProduct
                isOpen={newModalProduct}
                onClose={
                    () => {
                        setNewModalProduct(false);
                        fetchData(1, 10, "", null);
                    }
                }
            />

            <SearchTable
                columns={getColumns(t)}
                data={fData}
                dataLength={length}
                onRowClick={(data) => {
                    navigate(`/product/${data.id}`, {state: {id: data.id}})
                }}
                fetchData={(data) => fetchData(data.page, data.perPage, data.searchTerm, data.sortParams)}
                page={page}
                perPage={perPage}
                setPage={setPage}
                setPerPage={setPerPage}
                search={search}
                setSearch={setSearch}
                sortParams={sortParmas}
                setSortParams={setSortParams}
                navigationOnTop={true}
                disableMainSearch={sku.length > 0}
                additionFields={
                    <>
                        <div className="w-full flex flex-col md:flex-row mx-2 my-2 md:my-0">
                            <InputGroup size='md' className="mx-2">
                                <InputLeftElement pointerEvents='none'>
                                    <Icon as={SearchIcon} className="m-2"/>
                                </InputLeftElement>
                                <Input
                                    pr='4.5rem'
                                    type='text'
                                    disabled={search.length > 0}
                                    value={sku}
                                    onChange={(e) => setSku(e.target.value)}
                                    placeholder='SKU'

                                />
                            </InputGroup>
                        </div>
                        <CategorySelect
                            mainCategory={mainCategoryId}
                            onCategorySelect={(value) => setMainCategoryId(value)}
                            subCategory={subCategoriesIds}
                            onSubCategorySelect={(values) => {
                                setSubCategoriesIds([...values]);
                            }}
                        />
                    </>}
            />
        </>
    )
}

export default Products;